@file:OptIn(ExperimentalEncodingApi::class)
package com.pincer.core.functions

import com.pincer.core.InvalidData
import com.pincer.core.multiplatform.mpHmacSha256B64Url
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.time.Clock
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.SerializationException

// JWTs use unpadded base64url. Since Kotlin 2 the decoder is strict about padding unless told otherwise.
private val B64 = Base64.UrlSafe.withPadding(Base64.PaddingOption.ABSENT_OPTIONAL)

// See https://www.ietf.org/archive/id/draft-jones-json-web-token-02.html#base64urllogic
internal fun b64UrlNoPaddingEncode(string: String): String {
    return B64.encode(string.encodeToByteArray()).replace("=", "")
}

internal fun b64UrlDecode(string: String): String {
    return B64.decode(string).decodeToString()
}

@Serializable
data class Payload(val iss: String, val sub: String?, val exp: Long) {
    companion object {
        fun parse(json: String): Payload {
            try { return Json.decodeFromString<Payload>(serializer(), json) }
            catch (jde: SerializationException) { throw InvalidData("Problem parsing jwt payload") }
        }
        fun parseFromJwt(jwt: String) = parse(b64UrlDecode(jwt.split(".")[1]))
    }
    fun json(): String = Json.encodeToString(serializer(), this)
}

/*
Not part of the official validation process.
Just dig out the claimed subject/issuer for logging etc.
*/
fun extractIssuer(jwt: String): String? {
    try {return Payload.parseFromJwt(jwt).iss }
    catch (ignored: Exception) { return null }
}
fun extractSubject(jwt: String): String? {
    try {return Payload.parseFromJwt(jwt).sub }
    catch (ignored: Exception) { return null }
}

// {"alg":"HS256","typ":"JWT"}
private val HEADER = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9"

private fun epochSeconds() = Clock.System.now().toEpochMilliseconds() / 1000

private fun epochSecondsPlusFiveMins() = epochSeconds() + 300

fun buildJwt(iss: String, secret: String, sub: String?): String {
    val payload = b64UrlNoPaddingEncode(Payload(iss = iss, sub = sub, exp = epochSecondsPlusFiveMins()).json())
    val toSign = "$HEADER.$payload"
    val signature = mpHmacSha256B64Url(toSign, secret)
    return "$HEADER.$payload.$signature"
}
